import React, { useContext, useEffect } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import logger from '../logger';
import { BREAKPOINTS } from '../constants';

logger.info(BREAKPOINTS, 'BREAKPOINTS');

const getWindowDetails = () => {
  /*
   * Starting with just the width and height, but built in a way be flexible.
   * Use the minimum of innerWidth and the physical CSS pixel width to handle
   * mobile browsers that report a large virtual viewport without a viewport meta tag.
   */
  const { innerWidth, innerHeight: height } = window;
  // screen.width is in CSS logical pixels on all platforms.
  // Without a viewport meta tag, innerWidth can be a large virtual viewport (980-1280px).
  // Taking the min gives the actual device CSS width in both cases.
  const width = Math.min(innerWidth, screen.width);

  let resolutions = {
    desktopLarge: width >= BREAKPOINTS.desktopLarge,
    desktopWide: width >= BREAKPOINTS.desktopWide,
    desktop: width >= BREAKPOINTS.desktop,
    tabletLandscape: width >= BREAKPOINTS.tabletLandscape,
    tablet: width >= BREAKPOINTS.tablet,
    mobileLandscape: width >= BREAKPOINTS.mobileLandscape,
    mobile: width >= BREAKPOINTS.mobile,
  };

  const findResolution = (width) => {
    if (width >= BREAKPOINTS.desktopWide) {
      return 'desktopWide';
    }
    if (width >= BREAKPOINTS.desktopLarge) {
      return 'desktopLarge';
    }
    if (width >= BREAKPOINTS.desktop) {
      return 'desktop';
    }
    if (width >= BREAKPOINTS.tabletLandscape) {
      return 'tabletLandscape';
    }
    if (width >= BREAKPOINTS.tablet) {
      return 'tablet';
    }
    if (width >= BREAKPOINTS.mobileLandscape) {
      return 'mobileLandscape';
    }

    /*
     * default to mobile view
     */
    return 'mobile';
  };

  logger.info(resolutions, 'resolutions');

  const breakpoint = findResolution(width);

  return {
    breakpoint,
    width,
    height,
  };
};

const BreakPoints = () => {
  const globalServices = useContext(GlobalStateContext);
  
  // Context should always be available since GlobalStateProvider wraps Routes
  // But guard against SSR or edge cases
  if (!globalServices?.appState) {
    return null;
  }
  
  // Hooks must be called unconditionally - service should always exist here
  const [appState, sendAppState] = useActor(globalServices.appState);

  /*
   * Inital load - trigger dimensions
   */
  useEffect(() => {
    sendAppState({
      type: 'updateWindowDetails',
      value: getWindowDetails(),
    });
  }, [sendAppState]);

  /*
   * On window resize - trigger dimensions
   */
  useEffect(() => {
    function handleResize() {
      sendAppState({
        type: 'updateWindowDetails',
        value: getWindowDetails(),
      });
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sendAppState]);

  /*
   * Never return a component, only calculate state
   */
  return null;
};

export default BreakPoints;
